// src/components/ModuleAdmin.tsx
// Panel de administración para activar/desactivar módulos visibles a todos los empleados.
// Solo visible para usuarios con rol admin/cto/developer.

import React from 'react';
import { useModules } from '@/context/ModulesContext';
import { ToggleLeft, ToggleRight, ShieldCheck, Package } from 'lucide-react';

// Módulos que siempre están activos (no se pueden desactivar desde el panel)
const ALWAYS_ON = new Set(['config', 'dashboard']);

// Categorías para agrupar visualmente
const CATEGORIES: { label: string; keys: string[] }[] = [
  {
    label: 'Comunicación',
    keys: ['my-threads', 'chat', 'assistant', 'whatsapp-templates', 'bulk'],
  },
  {
    label: 'Gestión',
    keys: ['calendar', 'contacts', 'customers', 'leads', 'vacancies', 'candidates'],
  },
  {
    label: 'Datos & Análisis',
    keys: ['history', 'backend', 'dashboard'],
  },
  {
    label: 'Telefonía',
    keys: ['twilio', 'calls', 'call-history'],
  },
  {
    label: 'Desarrollo',
    keys: ['dev-tasks'],
  },
];

export function ModuleAdmin() {
  const { modules, isAdmin, toggle, loading } = useModules();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-white/50">
        <div className="animate-spin mr-2 h-5 w-5 border-2 border-white/30 border-t-white rounded-full" />
        Cargando módulos…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-3 text-white/40">
        <ShieldCheck size={32} />
        <p>Solo los administradores pueden gestionar los módulos.</p>
      </div>
    );
  }

  // Build a map for quick lookup
  const moduleMap = new Map(modules.map((m) => [m.key, m]));

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Package size={22} className="text-indigo-400" />
        <div>
          <h2 className="text-lg font-semibold text-white">Módulos del sistema</h2>
          <p className="text-xs text-white/50">
            Activa o desactiva módulos para todos los empleados de esta organización.
          </p>
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const items = cat.keys
          .map((k) => moduleMap.get(k))
          .filter(Boolean) as { key: string; label: string; enabled: boolean }[];

        if (items.length === 0) return null;

        return (
          <div
            key={cat.label}
            className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
          >
            <div className="px-4 py-2.5 bg-white/[0.04] border-b border-white/10 text-xs font-semibold uppercase tracking-wider text-white/50">
              {cat.label}
            </div>
            <div className="divide-y divide-white/5">
              {items.map((mod) => {
                const locked = ALWAYS_ON.has(mod.key);
                return (
                  <div
                    key={mod.key}
                    className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm text-white/90">{mod.label}</span>
                      <span className="text-[11px] text-white/30 font-mono">{mod.key}</span>
                    </div>
                    {locked ? (
                      <span className="text-[11px] text-white/30 italic">Siempre activo</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggle(mod.key, !mod.enabled)}
                        className={`transition-colors ${mod.enabled ? 'text-emerald-400 hover:text-emerald-300' : 'text-white/20 hover:text-white/40'}`}
                        title={mod.enabled ? 'Desactivar' : 'Activar'}
                      >
                        {mod.enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
